/**
 * Órdenes que un actuador puede aceptar.
 * `set` cubre encendido y apagado; `pulse` es un accionamiento momentáneo.
 */
export type AccionActuador = 'set' | 'pulse';

export interface ActuadorProps {
  id: number;
  nombre: string;
  codigoEnvio: string | null;
  tipoNombre: string | null;
  tipoCodigo: string | null;
  /**
   * Órdenes que este actuador admite, según su tipo.
   * Una lista vacía significa «sin restricción», no «ninguna»: los tipos que
   * no declaran acciones aceptan todas.
   */
  acciones: readonly AccionActuador[];
  /** El servidor puede bloquear el accionamiento por sus propias reglas. */
  puedeAccionar: boolean;
  /** Segundos que una orden sigue siendo válida; `null` si no caducan. */
  vigenciaSegundos: number | null;
  /**
   * Lecturas del estado, por origen. El equipo puede informar de varias
   * maneras y no siempre coinciden; `estadoOperativo` decide cuál manda.
   *
   * Se guardan tal cual llegan porque cada equipo las expresa a su modo:
   * texto, booleano, número o un objeto `{ v: … }`. Interpretarlas es tarea
   * de `interpretar`.
   */
  estadoFisico: unknown;
  origenFisico: string | null;
  fechaFisico: Date | null;
  estadoReportado: unknown;
  origenReportado: string | null;
  fechaReportado: Date | null;
  /** Estado que se le pidió al equipo, aún sin confirmar. */
  estadoDeseado: unknown;
  fechaUltimaConfirmacion: Date | null;
  /** Estado al que el equipo cae solo si pierde la comunicación. */
  estadoSeguro: unknown;
  /** Estado que calcula la propia vista; último recurso. */
  estadoVista: unknown;
  ultimaOrden: string | null;
  nodoId: number | null;
  nodoNombre: string | null;
  areaId: number | null;
  areaNombre: string | null;
  clienteId: number | null;
  clienteNombre: string | null;
  actualizadoEn: Date | null;
}

/** Duración mínima y máxima de un pulso, en milisegundos. */
export const PULSO_MINIMO_MS = 50;
export const PULSO_MAXIMO_MS = 30_000;

/**
 * Actuador: un equipo que se puede accionar a distancia.
 *
 * Qué órdenes admite lo decide su tipo, no la pantalla. Preguntárselo a la
 * entidad evita ofrecer un botón de pulso a un equipo que sólo entiende
 * encendido y apagado.
 */
export class Actuador {
  constructor(private readonly props: ActuadorProps) {}

  get id(): number {
    return this.props.id;
  }
  get nombre(): string {
    return this.props.nombre;
  }
  get codigoEnvio(): string | null {
    return this.props.codigoEnvio;
  }
  get tipo(): string {
    return this.props.tipoNombre ?? this.props.tipoCodigo ?? 'Sin tipo';
  }
  get vigenciaSegundos(): number | null {
    return this.props.vigenciaSegundos;
  }
  get nodoId(): number | null {
    return this.props.nodoId;
  }
  get nodoNombre(): string | null {
    return this.props.nodoNombre;
  }
  get areaId(): number | null {
    return this.props.areaId;
  }
  get areaNombre(): string | null {
    return this.props.areaNombre;
  }
  get clienteId(): number | null {
    return this.props.clienteId;
  }
  get clienteNombre(): string | null {
    return this.props.clienteNombre;
  }
  get actualizadoEn(): Date | null {
    return this.props.actualizadoEn;
  }

  /** Ubicación legible: «Cliente · Área · Nodo». */
  get ubicacion(): string {
    return (
      [this.props.clienteNombre, this.props.areaNombre, this.props.nodoNombre]
        .filter(Boolean)
        .join(' · ') || '—'
    );
  }

  /**
   * `true` si el actuador acepta esta orden.
   * Cuando el tipo no declara acciones se asume que las admite todas, que es
   * como se comporta el sistema actual.
   */
  admite(accion: AccionActuador): boolean {
    if (!this.props.puedeAccionar) return false;
    if (this.props.acciones.length === 0) return true;
    return this.props.acciones.includes(accion);
  }

  /** Encender y apagar son la misma orden `set` con distinto valor. */
  get admiteEncendido(): boolean {
    return this.admite('set');
  }
  get admiteApagado(): boolean {
    return this.admite('set');
  }
  get admitePulso(): boolean {
    return this.admite('pulse');
  }

  /** `true` si el equipo no acepta ninguna orden desde la consola. */
  get sinAcciones(): boolean {
    return !this.admiteEncendido && !this.admitePulso;
  }

  /**
   * Estado vigente del equipo y de dónde salió.
   *
   * El equipo informa por varias vías que pueden contradecirse, así que hay
   * que elegir. El criterio: gana la lectura más reciente entre la física y la
   * reportada, salvo que la física venga de un arranque (`boot_sync`), porque
   * ese es un valor guardado en memoria del equipo y puede ser viejo aunque su
   * marca de tiempo sea nueva. Si no hay ninguna medición, se muestra lo que
   * se pidió, luego el estado seguro y por último lo que calcule la vista.
   */
  get estadoOperativo(): { valor: unknown; origen: string } {
    const p = this.props;
    const hay = (v: unknown) => v !== null && v !== undefined && v !== '';

    const fisicoEsArranque = p.origenFisico === 'boot_sync';
    const hayFisico = hay(p.estadoFisico);
    const hayReportado = hay(p.estadoReportado);

    const tsFisico = p.fechaFisico?.getTime() ?? null;
    const tsReportado = p.fechaReportado?.getTime() ?? p.fechaUltimaConfirmacion?.getTime() ?? null;

    // Un arranque no debe pisar una confirmación posterior del equipo.
    if (fisicoEsArranque && hayReportado) {
      const reportadoEsMasNuevo =
        tsReportado !== null && tsFisico !== null && tsReportado >= tsFisico;

      if (reportadoEsMasNuevo || (p.origenReportado && p.origenReportado !== 'boot_sync')) {
        return { valor: p.estadoReportado, origen: 'Reportado por el equipo' };
      }
    }

    if (hayFisico && hayReportado && tsFisico !== null && tsReportado !== null) {
      return tsFisico >= tsReportado
        ? { valor: p.estadoFisico, origen: 'Medido en el equipo' }
        : { valor: p.estadoReportado, origen: 'Reportado por el equipo' };
    }

    if (hayFisico && !fisicoEsArranque) {
      return { valor: p.estadoFisico, origen: 'Medido en el equipo' };
    }
    if (hayReportado) {
      return { valor: p.estadoReportado, origen: 'Reportado por el equipo' };
    }
    if (hayFisico) {
      return { valor: p.estadoFisico, origen: 'Guardado al arrancar' };
    }
    if (hay(p.estadoDeseado)) {
      return { valor: p.estadoDeseado, origen: 'Última orden enviada' };
    }
    if (hay(p.estadoSeguro)) {
      return { valor: p.estadoSeguro, origen: 'Estado seguro' };
    }
    return { valor: p.estadoVista, origen: 'Estado' };
  }

  /** De dónde sale el estado que se está mostrando. */
  get origenEstado(): string {
    return this.estadoOperativo.origen;
  }

  /**
   * `true` si el equipo está encendido.
   * `null` cuando no ha informado, que es distinto de estar apagado.
   */
  get encendido(): boolean | null {
    return Actuador.interpretar(this.estadoOperativo.valor);
  }

  /** Texto del estado para mostrar en la tarjeta. */
  get estadoMostrable(): string {
    const { valor } = this.estadoOperativo;
    const encendido = Actuador.interpretar(valor);

    if (encendido !== null) return encendido ? 'ENCENDIDO' : 'APAGADO';

    // Un valor que no sabemos leer se muestra tal cual, nunca como objeto.
    if (valor === null || valor === undefined || valor === '') return 'Sin reportar';
    if (typeof valor === 'object') return JSON.stringify(valor);
    return String(valor);
  }

  /**
   * Traduce a encendido/apagado las muchas formas en que los equipos expresan
   * su estado: texto, booleano, número, o un objeto `{ v: … }`.
   * Devuelve `null` cuando el valor no significa ninguno de los dos.
   */
  private static interpretar(valor: unknown): boolean | null {
    if (valor === null || valor === undefined || valor === '') return null;

    if (typeof valor === 'boolean') return valor;
    if (typeof valor === 'number') {
      if (valor === 1) return true;
      if (valor === 0) return false;
      return null;
    }

    if (typeof valor === 'object') {
      // Forma habitual del payload de comandos: { v: "ON" }.
      const registro = valor as Record<string, unknown>;
      const interno = registro.v ?? registro.value ?? registro.estado;
      return interno === undefined ? null : Actuador.interpretar(interno);
    }

    const t = String(valor).trim();
    const tl = t.toLowerCase();

    if (['on', '1', 'true', 'encendido', 'abierto', 'closed'].includes(tl)) return true;
    if (['off', '0', 'false', 'apagado', 'cerrado', 'open'].includes(tl)) return false;

    // Puede venir un JSON serializado dentro de una cadena.
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        return Actuador.interpretar(JSON.parse(t));
      } catch {
        return null;
      }
    }

    return null;
  }

  coincideCon(texto: string): boolean {
    const t = texto.trim().toLowerCase();
    if (!t) return true;
    return [
      this.props.nombre,
      this.props.codigoEnvio,
      this.props.tipoNombre,
      this.props.nodoNombre,
      this.props.areaNombre,
      this.props.clienteNombre,
    ].some((campo) => campo?.toLowerCase().includes(t));
  }

  /** Comprueba que una duración de pulso esté dentro de lo que admite el equipo. */
  static validarPulso(ms: number): string | null {
    if (!Number.isFinite(ms)) return 'Indica una duración en milisegundos';
    if (ms < PULSO_MINIMO_MS) return `El pulso mínimo es de ${PULSO_MINIMO_MS} ms`;
    if (ms > PULSO_MAXIMO_MS) return `El pulso máximo es de ${PULSO_MAXIMO_MS} ms`;
    return null;
  }
}
